// /connect URLs carry the sign-in transaction, login hint and refused redirect target; /oauth/*
// request URLs carry the transaction too. RUM's documented way to redact is to mutate the event
// in place from beforeSend.

const CONNECT_PATH = '/connect';
const OAUTH_PREFIX = '/oauth/';

const parse = (raw: string): URL | null => {
  try {
    return new URL(raw, window.location.origin);
  } catch {
    return null;
  }
};

// Drops query and hash from a /connect URL; any other URL is returned untouched.
export const stripConnectQuery = (raw: string): string => {
  const url = parse(raw);
  if (!url || url.pathname !== CONNECT_PATH) return raw;
  url.search = '';
  url.hash = '';
  return url.toString();
};

// Drops the query from a /connect or /oauth/* URL; any other URL is returned untouched.
export const stripSensitiveQuery = (raw: string): string => {
  const url = parse(raw);
  if (!url || !(url.pathname === CONNECT_PATH || url.pathname.startsWith(OAUTH_PREFIX))) {
    return raw;
  }
  url.search = '';
  return url.toString();
};

interface RedactableEvent {
  view?: { url?: unknown; referrer?: unknown };
  resource?: { url?: unknown };
  error?: { resource?: { url?: unknown } };
}

const redactField = (
  holder: Record<string, unknown> | undefined,
  key: string,
  strip: (raw: string) => string
) => {
  const value = holder?.[key];
  if (holder && typeof value === 'string' && value) {
    // eslint-disable-next-line no-param-reassign
    holder[key] = strip(value);
  }
};

// Every RUM event type carries view.url and view.referrer; resource and error events also carry
// the request URL.
export const redactRumEvent = (event: RedactableEvent): void => {
  redactField(event.view, 'url', stripConnectQuery);
  redactField(event.view, 'referrer', stripConnectQuery);
  redactField(event.resource, 'url', stripSensitiveQuery);
  redactField(event.error?.resource, 'url', stripSensitiveQuery);
};
